import React, { useEffect, useRef, useState } from "react";
import type { OrganizationComputeResource } from "server/devplane/organizations/api";
import { Button } from "components/Graphs/Button";
import { FullScreenLoadingSpinner } from "components/FullScreenLoader";
import { CloudComputeCard, LocalComputeCard } from "./Compute/index";
import ComputeEmptyState, {
  type ComputeEmptyStateKind,
} from "./ComputeEmptyState";

const computeRowEntranceDurationMs = 320;
const computeRowEntranceStaggerMs = 40;
const computeRowEntranceMaxDelayMs = 350;

const getComputeRowEntranceDelay = (index: number, rowCount: number) => {
  if (rowCount <= 1) return 0;
  const stagger = Math.min(
    computeRowEntranceStaggerMs,
    computeRowEntranceMaxDelayMs / (rowCount - 1),
  );
  return Math.round(index * stagger);
};

const ComputeLoadError: React.FC<{
  description: string;
  onRetry: () => void;
  title: string;
}> = ({ description, onRetry, title }) => (
  <div className="rounded-lg border border-zinc-800 bg-zinc-900/30 py-8 text-center">
    <h3 className="text-sm font-semibold text-zinc-100">{title}</h3>
    <p className="mx-auto mt-2 max-w-md text-sm text-zinc-400">{description}</p>
    <Button variant="secondary" className="mt-5" onClick={onRetry}>
      Retry
    </Button>
  </div>
);

interface ComputePanelProps {
  emptyStateKind: ComputeEmptyStateKind;
  isAdmin: boolean;
  isConnectivityLoading: boolean;
  isError: boolean;
  isLoading: boolean;
  onAddCloud: () => void;
  onRetry: () => void;
  resources: OrganizationComputeResource[];
  selectedComputeLabel: string;
}

const ComputePanel: React.FC<ComputePanelProps> = ({
  emptyStateKind,
  isAdmin,
  isConnectivityLoading,
  isError,
  isLoading,
  onAddCloud,
  onRetry,
  resources,
  selectedComputeLabel,
}) => {
  const [animateInitialRows, setAnimateInitialRows] = useState(true);
  const initialLoadHandledRef = useRef(false);
  const animationTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      if (animationTimeoutRef.current !== undefined) {
        window.clearTimeout(animationTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (initialLoadHandledRef.current || isLoading) return;
    initialLoadHandledRef.current = true;
    if (resources.length === 0) {
      animationTimeoutRef.current = window.setTimeout(
        () => setAnimateInitialRows(false),
        0,
      );
      return;
    }

    const finalRowDelay = getComputeRowEntranceDelay(
      resources.length - 1,
      resources.length,
    );
    animationTimeoutRef.current = window.setTimeout(
      () => setAnimateInitialRows(false),
      finalRowDelay + computeRowEntranceDurationMs,
    );
  }, [isLoading, resources.length]);

  return (
    <div
      id="compute-panel"
      role="tabpanel"
      aria-labelledby="compute-tab"
      className="py-6"
    >
      {isLoading ? (
        <FullScreenLoadingSpinner
          heightClassName="h-32"
          spinnerClassName="mt-60"
        />
      ) : resources.length ? (
        <div className="space-y-6">
          {resources.map((resource, index) => (
            <div
              className={
                animateInitialRows
                  ? "motion-safe:animate-compute-row-enter"
                  : undefined
              }
              key={
                resource.type === "cloud"
                  ? `cloud:${resource.environment.workspaceId}`
                  : `local:${resource.node.externalNodeId}`
              }
              style={
                animateInitialRows
                  ? {
                      animationDelay: `${getComputeRowEntranceDelay(index, resources.length)}ms`,
                    }
                  : undefined
              }
            >
              {resource.type === "cloud" ? (
                <CloudComputeCard environment={resource.environment} />
              ) : (
                <LocalComputeCard
                  node={resource.node}
                  isConnectivityLoading={isConnectivityLoading}
                />
              )}
            </div>
          ))}
        </div>
      ) : isError ? (
        <ComputeLoadError
          title={`${selectedComputeLabel[0].toUpperCase()}${selectedComputeLabel.slice(1)} could not be loaded`}
          description={`There was an issue loading ${selectedComputeLabel} for this organization.`}
          onRetry={onRetry}
        />
      ) : (
        <ComputeEmptyState
          isAdmin={isAdmin}
          kind={emptyStateKind}
          onAddCloud={onAddCloud}
          selectedComputeLabel={selectedComputeLabel}
        />
      )}
    </div>
  );
};

export default ComputePanel;
